import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from './ui/card';
import {
  Wallet,
  Plus,
  Send,
  ArrowUpRight,
  ArrowDownLeft,
  RefreshCw,
  Copy,
  ExternalLink,
  Shield,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Zap,
  QrCode,
  Globe,
  CreditCard,
  ArrowLeft
} from 'lucide-react';

const MemberCrypto = ({ user, onNavigate }) => {
  const [wallets, setWallets] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showCreateWallet, setShowCreateWallet] = useState(false);
  const [showSendCrypto, setShowSendCrypto] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [hideBalances, setHideBalances] = useState(false);
  const [activeTab, setActiveTab] = useState('wallets');
  const [showFiatGateway, setShowFiatGateway] = useState(false);
  const [fiatAmount, setFiatAmount] = useState('');
  const [selectedCrypto, setSelectedCrypto] = useState('USDT');
  const [conversionType, setConversionType] = useState('buy'); // 'buy' or 'sell'

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  useEffect(() => {
    fetchWallets();
    fetchTransactions();
  }, []);

  const fetchWallets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/crypto/wallets`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.wallets) {
        setWallets(response.data.wallets);
      }
    } catch (error) {
      console.error('Error fetching wallets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API}/crypto/transactions`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.transactions) {
        setTransactions(response.data.transactions);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
    }
  };

  const refreshWalletBalances = async (walletAddress, network) => {
    setRefreshing(true);
    try {
      const response = await axios.post(`${API}/crypto/wallets/${walletAddress}/refresh?network=${network}`, {}, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.wallet) {
        // Update the wallet in the list
        setWallets(prev => prev.map(wallet => 
          wallet.address === walletAddress ? response.data.wallet : wallet
        ));
      }
    } catch (error) {
      console.error('Error refreshing wallet:', error);
    } finally {
      setRefreshing(false);
    }
  };

  const createNewWallet = async (network = 'polygon') => {
    try {
      const response = await axios.post(`${API}/crypto/wallet`, { network }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data.wallet) {
        setWallets(prev => [...prev, response.data.wallet]);
        setShowCreateWallet(false);
      }
    } catch (error) {
      console.error('Error creating wallet:', error);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // Add toast notification here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatBalance = (balance) => {
    if (hideBalances) return '****';
    return parseFloat(balance).toFixed(4);
  };

  const formatCurrency = (amount, symbol = 'USDT') => {
    if (hideBalances) return '****';
    return `${parseFloat(amount).toFixed(2)} ${symbol}`;
  };

  const getTotalBalance = () => {
    return wallets.reduce((total, wallet) => {
      return total + wallet.balance_usdt + wallet.balance_usdc;
    }, 0);
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  const getNetworkIcon = (network) => {
    const icons = {
      ethereum: '⟠',
      polygon: '⬟',
      bsc: '🟡'
    };
    return icons[network] || '🌐';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p>Loading your crypto wallets...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <div className="container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button 
            onClick={() => onNavigate && onNavigate('dashboard')}
            className="flex items-center gap-2 text-slate-300 hover:text-white px-3 py-2 rounded-md hover:bg-slate-800 transition-colors"
            aria-label="Retour"
          >
            <ArrowLeft size={20} />
            <span>Retour</span>
          </button>
          <div className="flex-1 text-center">
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center justify-center space-x-3">
              <Wallet className="w-8 h-8" />
              <span>Crypto Wallet</span>
            </h1>
            <p className="text-slate-300">Manage your cryptocurrency assets and transactions</p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setHideBalances(!hideBalances)}
              className="p-3 bg-white/10 rounded-full backdrop-blur-sm hover:bg-white/20 transition-all"
            >
              {hideBalances ? <EyeOff className="w-5 h-5 text-white" /> : <Eye className="w-5 h-5 text-white" />}
            </button>
            <button
              onClick={() => setShowCreateWallet(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Create Wallet</span>
            </button>
          </div>
        </div>

        {/* Portfolio Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-300 text-sm">Total Balance</p>
                  <p className="text-2xl font-bold">{formatCurrency(getTotalBalance())}</p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-full">
                  <DollarSign className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-300 text-sm">Active Wallets</p>
                  <p className="text-2xl font-bold">{wallets.length}</p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-full">
                  <Wallet className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-300 text-sm">Transactions</p>
                  <p className="text-2xl font-bold">{transactions.length}</p>
                </div>
                <div className="p-3 bg-purple-500/20 rounded-full">
                  <TrendingUp className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-8 bg-white/10 rounded-lg p-1">
          {['wallets', 'transactions', 'convert'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 px-4 py-2 rounded-md transition-all capitalize ${
                activeTab === tab 
                  ? 'bg-white/20 text-white' 
                  : 'text-slate-300 hover:text-white hover:bg-white/10'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'wallets' && (
          <div className="space-y-6">
            {wallets.length === 0 ? (
              <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
                <CardContent className="p-12 text-center">
                  <Wallet className="w-16 h-16 text-slate-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold mb-2">No Wallets Yet</h3>
                  <p className="text-slate-300 mb-6">Create your first crypto wallet to get started</p>
                  <button
                    onClick={() => setShowCreateWallet(true)}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg text-white transition-colors"
                  >
                    Create Wallet
                  </button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {wallets.map((wallet) => (
                  <Card key={`${wallet.address}-${wallet.network}`} className="bg-white/10 backdrop-blur-md border-white/20 text-white">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <span className="text-2xl">{getNetworkIcon(wallet.network)}</span>
                          <span className="capitalize">{wallet.network}</span>
                        </div>
                        <button
                          onClick={() => refreshWalletBalances(wallet.address, wallet.network)}
                          disabled={refreshing}
                          className="p-2 hover:bg-white/10 rounded-full transition-all"
                        >
                          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                        </button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center space-x-2 p-2 bg-white/5 rounded-lg">
                        <span className="text-xs text-slate-400 font-mono flex-1">
                          {wallet.address.substring(0, 6)}...{wallet.address.substring(wallet.address.length - 4)}
                        </span>
                        <button
                          onClick={() => copyToClipboard(wallet.address)}
                          className="p-1 hover:bg-white/10 rounded transition-all"
                        >
                          <Copy className="w-4 h-4 text-slate-400" />
                        </button>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center p-3 bg-white/5 rounded-lg">
                          <p className="text-slate-400 text-sm">USDT Balance</p>
                          <p className="text-lg font-bold text-yellow-400">{formatBalance(wallet.balance_usdt)}</p>
                        </div>
                        <div className="text-center p-3 bg-white/5 rounded-lg">
                          <p className="text-slate-400 text-sm">USDC Balance</p>
                          <p className="text-lg font-bold text-blue-400">{formatBalance(wallet.balance_usdc)}</p>
                        </div>
                      </div>

                      <div className="flex space-x-3">
                        <button
                          onClick={() => {
                            setSelectedWallet(wallet);
                            setShowSendCrypto(true);
                          }}
                          className="flex-1 flex items-center justify-center space-x-2 p-3 bg-blue-600/20 hover:bg-blue-600/30 text-blue-300 rounded-lg transition-all"
                        >
                          <Send className="w-4 h-4" />
                          <span>Send</span>
                        </button>
                        <button className="flex-1 flex items-center justify-center space-x-2 p-3 bg-green-600/20 hover:bg-green-600/30 text-green-300 rounded-lg transition-all">
                          <QrCode className="w-4 h-4" />
                          <span>Receive</span>
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'transactions' && (
          <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
            <CardHeader>
              <CardTitle>Transaction History</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-300 mb-2">No transactions yet</p>
                  <p className="text-sm text-slate-500">Your transaction history will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {transactions.map((tx) => (
                    <div key={tx.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg hover:bg-white/10 transition-all">
                      <div className="flex items-center space-x-4">
                        <div className={`p-2 rounded-full ${
                          tx.transaction_type === 'contribution' ? 'bg-green-500/20' : 'bg-blue-500/20'
                        }`}>
                          {tx.transaction_type === 'contribution' ? (
                            <ArrowUpRight className="w-5 h-5 text-green-400" />
                          ) : (
                            <ArrowDownLeft className="w-5 h-5 text-blue-400" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-white capitalize">{tx.transaction_type}</p>
                          <p className="text-sm text-slate-400">{formatTimeAgo(tx.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-white">{tx.amount} {tx.token_symbol}</p>
                        <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                          tx.status === 'confirmed' ? 'bg-green-500/20 text-green-300' :
                          tx.status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                          'bg-red-500/20 text-red-300'
                        }`}>
                          {tx.status === 'confirmed' ? (
                            <CheckCircle className="w-3 h-3 mr-1" />
                          ) : tx.status === 'pending' ? (
                            <Clock className="w-3 h-3 mr-1" />
                          ) : (
                            <AlertCircle className="w-3 h-3 mr-1" />
                          )}
                          {tx.status}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'convert' && (
          <Card className="bg-white/10 backdrop-blur-md border-white/20 text-white">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CreditCard className="w-5 h-5" />
                <span>Fiat to Crypto</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center py-8">
                <Globe className="w-16 h-16 text-slate-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Fiat Gateway Coming Soon</h3>
                <p className="text-slate-300 mb-6">Convert your fiat currency to cryptocurrency directly from your bank account</p>
                <div className="space-y-2 text-sm text-slate-400">
                  <p>• Support for KES, USD, EUR</p>
                  <p>• Instant conversion to USDT/USDC</p>
                  <p>• Low fees and fast processing</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Wallet Modal */}
        {showCreateWallet && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-lg max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-white mb-4">Create New Wallet</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Network</label>
                  <select className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="polygon">Polygon (MATIC)</option>
                    <option value="ethereum">Ethereum (ETH)</option>
                    <option value="bsc">Binance Smart Chain (BNB)</option>
                  </select>
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowCreateWallet(false)}
                    className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => createNewWallet('polygon')}
                    className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    Create
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberCrypto;